import { useCallback, useSyncExternalStore } from 'react';

export type ListDensity = 'normal' | 'compact';

const KEY = 'tenders:list-density';
// Same-tab change signal — the native `storage` event only fires in OTHER tabs.
const CHANGE_EVENT = 'tenders:list-density-change';

/**
 * Session override that wins over storage. Set when the user toggles, so the
 * choice still takes effect for the current session even when localStorage is
 * unavailable (private mode) and the write silently fails.
 */
let sessionValue: ListDensity | null = null;

function readDensity(): ListDensity {
  if (sessionValue !== null) return sessionValue;
  try {
    return localStorage.getItem(KEY) === 'compact' ? 'compact' : 'normal';
  } catch {
    // Private mode / storage disabled — the preference simply does not stick.
    return 'normal';
  }
}

/** Test-only: clear the session override so each test starts from storage. */
export function __resetListDensityForTests(): void {
  sessionValue = null;
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('storage', onChange);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
}

/**
 * How tightly the tender list is packed. A pure display preference.
 *
 * Backed by `useSyncExternalStore` (not a `useState` initialiser that reads
 * `localStorage`): the catalog route is server-rendered, and a synchronous
 * storage read at first client render diverged from the SSR default `'normal'`
 * for anyone who had chosen `'compact'` — a React #418 hydration mismatch that
 * made React discard and rebuild the whole list subtree (a large mobile CLS,
 * invisible to lab Lighthouse). With a `getServerSnapshot` of `'normal'`, the
 * client hydrates matching the server, then re-reads the client snapshot in a
 * passive effect — applying the stored preference as an ordinary update, with
 * no mismatch and no subtree rebuild.
 *
 * Deliberately NOT a URL param: it changes nothing about which tenders are
 * returned, and putting it in the query string would fork every prerendered
 * catalog URL into two that serve identical content — a duplicate-content
 * problem on a site where only a fraction of pages are indexed today.
 */
export function useListDensity(): [ListDensity, (d: ListDensity) => void] {
  const density = useSyncExternalStore<ListDensity>(subscribe, readDensity, () => 'normal');

  const setDensity = useCallback((next: ListDensity) => {
    sessionValue = next;
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* private mode — the session override above still honours the choice */
    }
    // The native `storage` event does not fire in the same tab; nudge subscribers.
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return [density, setDensity];
}
